import { useState } from "react";
import { Link } from "react-router-dom";
import StarField from "@/components/StarField";
import TopBar from "@/components/TopBar";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      // Backend always returns success — never leaks whether email exists
      setSent(true);
      toast.success("If this email is registered, a new key has been sent.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen">
      <StarField count={120} />
      <TopBar />
      <main className="relative z-20 flex items-center justify-center px-6 py-16">
        <div className="glass-strong rounded-sm p-8 sm:p-12 w-full max-w-md" data-testid="forgot-password-panel">
          <div className="text-center">
            <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-cyan-400/80 mb-2">
              — forge a new key —
            </div>
            <h1 className="font-display text-3xl font-black uppercase" data-testid="forgot-password-heading">Forgot Your Key</h1>
            <p className="font-mono text-[11px] uppercase tracking-widest text-white/50 mt-4">
              Enter your email — we&#39;ll send a new key straight to your inbox
            </p>
          </div>

          {sent ? (
            <div className="mt-8 space-y-4" data-testid="forgot-password-sent-state">
              <div className="glass rounded-sm p-6 border border-cyan-400/20">
                <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-cyan-400/80">— sent —</div>
                <p className="font-mono text-sm text-white/70 mt-3 leading-relaxed">
                  If <span className="text-cyan-400">{email}</span> is registered, a new temporary key is on its way to that inbox.
                  Sign in with it — then change it from your dashboard to something only you know.
                </p>
                <p className="font-mono text-[11px] text-white/40 mt-3">
                  Check spam if it doesn&#39;t appear within a minute.
                </p>
              </div>
              <Link
                to="/login"
                data-testid="forgot-password-back-to-login"
                className="block text-center gate w-full py-3 bg-white text-black font-display font-bold uppercase tracking-[0.3em] text-xs rounded-sm hover:scale-[1.02] transition"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <form onSubmit={submit} className="mt-8 space-y-4">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  data-testid="forgot-password-email-input"
                  className="w-full px-4 py-3 bg-black/60 border border-white/15 rounded-sm font-mono text-sm focus:outline-none focus:border-cyan-400/80"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-password-submit-btn"
                  className="gate w-full py-3 bg-white text-black font-display font-bold uppercase tracking-[0.3em] text-xs rounded-sm hover:scale-[1.02] transition disabled:opacity-50"
                >
                  {loading ? "..." : "Send Me A New Key"}
                </button>
              </form>
              <div className="text-center font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mt-6">
                <Link
                  to="/login"
                  data-testid="forgot-password-back-link"
                  className="text-cyan-400 hover:underline"
                >
                  Back to sign in
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
